/**
 * The Docs tab (`/{ws}/docs`, logic/docsList): all my pages and databases in one searchable list with filters (All,
 * Recent, Created by me, Favorites, Databases). Reads the page tree's cache, so renames, new pages and trash show up
 * live; creating a page goes through PageTreeViewModel.
 */
import type { PageNode } from "@bundle/contracts";
import { type DocsFilter, docsList, docsPath } from "../logic/docsList.ts";
import type { PageTreeViewModel } from "./PageTreeViewModel.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

export type DocsRow = { page: PageNode; path: string; favorite: boolean };

export type DocsViewState = {
  status: "loading" | "ready" | "error";
  filter: DocsFilter;
  query: string;
  rows: DocsRow[];
  canCreatePages: boolean;
};

export class DocsViewModel extends ViewModel<DocsViewState> {
  constructor(private readonly ws: WorkspaceViewModel, private readonly tree: PageTreeViewModel) {
    super({ status: tree.state.status, filter: "all", query: "", rows: [], canCreatePages: tree.state.canCreatePages });
    this.onDispose(tree.subscribe(() => this.derive()));
    this.derive();
  }

  setFilter(filter: DocsFilter): void {
    this.update({ filter });
    this.derive();
  }

  setQuery(query: string): void {
    this.update({ query });
    this.derive();
  }

  private derive(): void {
    const cache = this.tree.state.cache;
    const t = this.tree.state;
    if (cache === undefined) {
      this.update({ status: t.status, canCreatePages: t.canCreatePages });
      return;
    }
    const favs = new Set(cache.favoriteIds);
    const rows = docsList(cache, this.state.filter, this.state.query, this.ws.state.meId, this.ws.deps.clock.now()).map((page) => ({
      page, path: docsPath(cache, page), favorite: favs.has(page.id),
    }));
    this.update({ status: t.status, rows, canCreatePages: t.canCreatePages });
  }
}
